import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, Dispatch, SetStateAction } from 'react';
import { useAuth } from '../../../contexts/authContext';
import { obtenerParametros } from '../services/obtenerParametros.service';

// Define the shape of your single state object
export type CierreFinancieroState = {
    [x: string]: ReactNode;
    totalIngresosAdicionales: number;
    salarioMensual: number;
    valorVivienda: number;
    tiempoSeleccionado: number | null;
    municipioVive: string;
    municipioCompra: string;
    deudas: number;
    pagoMensual: number;
    cesantias: number;
    ahorros: number;
    primas: string;
    cantidadPrimas: number;
    subsidioEmpresa: number;
    ahorroMensual: number;
    cesantiasFuturas: string;
    ingresosAdicionales: string;
    tasaEfectivaAnual: number;
    plazoCredito: number;
    porcentajeEndeudamiento: number;
    subsidioDepartamento: number;
    totalCreditoHipotecario: number;
    // Valores calculados para proyección y resultados
    totalAhorrosValue: number;
    sumaSubsidiosValue: number;
    valorTotalCreditoValue: number;
    subsidioCajaValue: number;
    subsidioMunicipioValue: number;
    valorSalarioMinimo: number;
    topeSubsidio: number;
};

export type CierreFinancieroContextType = {
    cierreFinanciero: CierreFinancieroState | null;
    setCierreFinanciero: Dispatch<SetStateAction<CierreFinancieroState | null>>;
};

export type CierreFinancieroProviderProps = {
    children?: ReactNode;
};



const STORAGE_KEY = 'cierreFinanciero';

export const CierreFinancieroContext = createContext<CierreFinancieroContextType | undefined>(undefined);

export const CierreFinancieroProvider = ({ children }: CierreFinancieroProviderProps) => {
    const [error, setError] = useState<string | null>(null);
    // Detecta ambiente
    const env = typeof process !== 'undefined' && process.env && process.env.GATSBY_ACTIVE_ENV ? process.env.GATSBY_ACTIVE_ENV : 'local';
    // Datos mock para Dev y local
    const mockCierreFinanciero: CierreFinancieroState = {
        totalIngresosAdicionales: 100000,
        salarioMensual: 2500000,
        valorVivienda: 180000000,
        tiempoSeleccionado: 12,
        municipioVive: 'Medellín',
        municipioCompra: 'Medellín',
        deudas: 0,
        pagoMensual: 0,
        cesantias: 5000000,
        ahorros: 10000000,
        primas: 'sí',
        cantidadPrimas: 2,
        subsidioEmpresa: 2000000,
        ahorroMensual: 500000,
        cesantiasFuturas: 'sí',
        ingresosAdicionales: 'freelance',
        tasaEfectivaAnual: 10.6,
        plazoCredito: 20,
        porcentajeEndeudamiento: 0.4,
        subsidioDepartamento: 0,
        totalCreditoHipotecario: 0,
        totalAhorrosValue: 10000000,
        sumaSubsidiosValue: 2000000,
        valorTotalCreditoValue: 150000000,
        subsidioCajaValue: 1000000,
        subsidioMunicipioValue: 1000000,
        valorSalarioMinimo: 1300000,
        topeSubsidio: 30000000,
    };
    // Restaurar estado desde sessionStorage si existe, o usar mock en dev/local
    const [cierreFinanciero, setCierreFinanciero] = useState<CierreFinancieroState | null>(() => {
        if (typeof window !== 'undefined') {
            const stored = sessionStorage.getItem(STORAGE_KEY);
            if (stored) {
                try {
                    return JSON.parse(stored);
                } catch (e) {
                    console.error('Error al parsear cierreFinanciero de sessionStorage:', e);
                }
            }
        }
        if ((env === 'dev' || env === 'local') && process.env.NODE_ENV !== 'test') {
            return mockCierreFinanciero;
        }
        return null;
    });

    const { user } = useAuth();
    useEffect(() => {
        if (cierreFinanciero !== null) return; // Si ya hay datos, no inicializar
        if (!user || !user.isAuthenticated || !user.gigyaAccount) return; // Solo si está autenticado y tiene gigyaAccount
        const fetchParametros = async () => {
            try {
                const parametros = await obtenerParametros();
                // Validación básica de la respuesta
                if (!parametros || typeof parametros !== 'object') {
                    setError('Respuesta de parámetros inválida');
                    return;
                }
                if (parametros.status === 200 && Array.isArray(parametros.data)) {
                    // Convierte el array de parametros en un objeto clave-valor validando cada campo
                    const paramObj = parametros.data.reduce((acc: any, item: any) => {
                        if (!item || typeof item.name !== 'string') return acc;
                        acc[item.name] = isNaN(Number(item.value)) ? item.value : Number(item.value);
                        return acc;
                    }, {});
                    setCierreFinanciero({
                        totalIngresosAdicionales: 0,
                        salarioMensual: 0,
                        valorVivienda: 0,
                        tiempoSeleccionado: null,
                        municipioVive: '',
                        municipioCompra: '',
                        deudas: 0,
                        pagoMensual: 0,
                        cesantias: 0,
                        ahorros: 0,
                        primas: 'no',
                        cantidadPrimas: 0,
                        subsidioEmpresa: 0,
                        ahorroMensual: 0,
                        cesantiasFuturas: 'no',
                        ingresosAdicionales: '',
                        tasaEfectivaAnual: typeof paramObj.tasaEfectivaAnual === 'number' && paramObj.tasaEfectivaAnual,
                        plazoCredito: typeof paramObj.plazoCredito === 'number' && paramObj.plazoCredito,
                        porcentajeEndeudamiento: typeof paramObj.porcentajeEndeudamiento === 'number' && paramObj.porcentajeEndeudamiento,
                        subsidioDepartamento: typeof paramObj.subsidioDepartamento === 'number' && paramObj.subsidioDepartamento,
                        totalCreditoHipotecario: 0,
                        totalAhorrosValue: 0,
                        sumaSubsidiosValue: 0,
                        valorTotalCreditoValue: 0,
                        subsidioCajaValue: typeof paramObj.subsidioCajaValue === 'number' && paramObj.subsidioCajaValue,
                        subsidioMunicipioValue: typeof paramObj.subsidioMunicipioValue === 'number' && paramObj.subsidioMunicipioValue,
                        valorSalarioMinimo: typeof paramObj.valorSalarioMinimo === 'number' && paramObj.valorSalarioMinimo,
                        topeSubsidio: typeof paramObj.topeSubsidio === 'number' && paramObj.topeSubsidio
                    });
                } else {
                    setError('Respuesta inesperada de parámetros');
                }
            } catch (error) {
                setError('Error al obtener parámetros');
            }
        };
        fetchParametros();
    }, [user, cierreFinanciero]);

    // Sincroniza los valores calculados automáticamente en el contexto
    useEffect(() => {
        if (!cierreFinanciero) return;
        // Importa aquí para evitar problemas de dependencias circulares
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { totalAhorros, subsidioCaja, subsidioMunicipio, sumaSubsidios, valorTotalCredito } = require('../utils/calculosFinancieros');
        const subsidioCajaValue = subsidioCaja(cierreFinanciero.salarioMensual);
        const subsidioMunicipioValue = subsidioMunicipio(cierreFinanciero.municipioCompra, subsidioCajaValue);
        const sumaSubsidiosValue = sumaSubsidios(subsidioCajaValue, subsidioMunicipioValue);
        const valorTotalCreditoValue = valorTotalCredito(cierreFinanciero);
        const totalAhorrosValue = totalAhorros(cierreFinanciero);
        setCierreFinanciero((prev) => prev ? ({
            ...prev,
            totalAhorrosValue,
            sumaSubsidiosValue,
            valorTotalCreditoValue,
            subsidioCajaValue,
            subsidioMunicipioValue,
        }) : prev);
        // Solo cuando cambian los datos base relevantes
    }, [
        cierreFinanciero?.salarioMensual,
        cierreFinanciero?.municipioCompra,
        cierreFinanciero?.valorVivienda,
        cierreFinanciero?.tiempoSeleccionado,
        cierreFinanciero?.ahorros,
        cierreFinanciero?.ahorroMensual,
        cierreFinanciero?.cantidadPrimas,
        cierreFinanciero?.cesantias,
        cierreFinanciero?.cesantiasFuturas,
        cierreFinanciero?.deudas,
        cierreFinanciero?.pagoMensual,
        cierreFinanciero?.subsidioEmpresa,
        cierreFinanciero?.subsidioDepartamento,
        cierreFinanciero?.totalIngresosAdicionales,
        cierreFinanciero?.ingresosAdicionales,
        cierreFinanciero?.primas,
        cierreFinanciero?.plazoCredito,
        cierreFinanciero?.tasaEfectivaAnual,
        cierreFinanciero?.porcentajeEndeudamiento,
    ]);

    // Persist to sessionStorage on every change
    // Debounce para evitar escrituras excesivas en sessionStorage
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
        if (!cierreFinanciero) return;
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cierreFinanciero));
        }, 300);
        // Limpieza al desmontar
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [cierreFinanciero]);

    return (
        <CierreFinancieroContext.Provider value={{ cierreFinanciero, setCierreFinanciero }}>
            {error && <div style={{color:'red'}}>{error}</div>}
            {children}
        </CierreFinancieroContext.Provider>
    );
};

export const useCierreFinanciero = () => {
    const context = useContext(CierreFinancieroContext);
    if (!context) {
        throw new Error('useCierreFinanciero must be used within a CierreFinancieroProvider');
    }
    return context;
};

// getInitialState ahora es asíncrono y toma valores de fetchParametros si no hay sessionStorage
export const getInitialState = async (): Promise<CierreFinancieroState> => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
        try {
            return JSON.parse(stored);
        } catch {
            // fallback to defaults if corrupted
        }
    }
    try {
        const parametros = await obtenerParametros();
        if (parametros.status === 200 && Array.isArray(parametros.data)) {
            const paramObj = parametros.data.reduce((acc: any, item: any) => {
                if (!item || typeof item.name !== 'string') return acc;
                acc[item.name] = isNaN(Number(item.value)) ? item.value : Number(item.value);
                return acc;
            }, {});
            return {
                totalIngresosAdicionales: 0,
                salarioMensual: 0,
                valorVivienda: 0,
                tiempoSeleccionado: null,
                municipioVive: '',
                municipioCompra: '',
                deudas: 0,
                pagoMensual: 0,
                cesantias: 0,
                ahorros: 0,
                primas: 'no',
                cantidadPrimas: 0,
                subsidioEmpresa: 0,
                ahorroMensual: 0,
                cesantiasFuturas: 'no',
                ingresosAdicionales: '',
                tasaEfectivaAnual: typeof paramObj.tasaEfectivaAnual === 'number' && paramObj.tasaEfectivaAnual,
                plazoCredito: typeof paramObj.plazoCredito === 'number' && paramObj.plazoCredito,
                porcentajeEndeudamiento: typeof paramObj.porcentajeEndeudamiento === 'number' && paramObj.porcentajeEndeudamiento,
                subsidioDepartamento: typeof paramObj.subsidioDepartamento === 'number' &&paramObj.subsidioDepartamento ,
                totalCreditoHipotecario: 0,
                totalAhorrosValue: 0,
                sumaSubsidiosValue: 0,
                valorTotalCreditoValue: 0,
                subsidioCajaValue: typeof paramObj.subsidioCajaValue === 'number' && paramObj.subsidioCajaValue,
                subsidioMunicipioValue: typeof paramObj.subsidioMunicipioValue === 'number' &&paramObj.subsidioMunicipioValue,
                valorSalarioMinimo: typeof paramObj.valorSalarioMinimo === 'number' && paramObj.valorSalarioMinimo,
                topeSubsidio: typeof paramObj.topeSubsidio === 'number' && paramObj.topeSubsidio,
            };
        }
    } catch {
        // fallback to defaults if error
    }
    return {
        totalIngresosAdicionales: 0,
        salarioMensual: 0,
        valorVivienda: 0,
        tiempoSeleccionado: null,
        municipioVive: '',
        municipioCompra: '',
        deudas: 0,
        pagoMensual: 0,
        cesantias: 0,
        ahorros: 0,
        primas: 'no',
        cantidadPrimas: 0,
        subsidioEmpresa: 0,
        ahorroMensual: 0,
        cesantiasFuturas: 'no',
        ingresosAdicionales: '',
        tasaEfectivaAnual: 10.6,
        plazoCredito: 20,
        porcentajeEndeudamiento: 0.4,
        subsidioDepartamento: 0,
        totalCreditoHipotecario: 0,
        totalAhorrosValue: 0,
        sumaSubsidiosValue: 0,
        valorTotalCreditoValue: 0,
        subsidioCajaValue: 0,
        subsidioMunicipioValue: 0,
        valorSalarioMinimo: 0,
        topeSubsidio: 0,
    };
};

